import React, { useState } from 'react';
import { Alert, Button, Col, Form, FormField, FormRow, formWithValidation, Right, Row } from '@edgebox/react-components';
import { type IOptionalSerializedReference, type ISerializedReference, InternalId } from '@edgebox/data-definition-kit';
import {
  AccessScope,
  AiModelUseCase,
  AiPromptOutputFormat,
  AiPromptOutputTextFormat,
  AiPromptTemplateStatus,
  type ISyncCoreAiPromptTemplateDTO,
  type ISyncCoreSerializedAiPromptTemplateProperties,
  SyncCoreAiPromptOutput,
} from '@edgebox/sync-core-data-definitions';
import type { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { ClientAiPromptTemplateDraft, ClientAiPromptTemplateEntity } from '@edgebox/sync-core-rest-client';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { enumValues, normalizeFilterId } from '../aiHelpers';
import { AiModelSelector } from './AiModelSelector';
import { AiProviderSelector } from './AiProviderSelector';

type IAiPromptTemplateDraftFormValues = ISyncCoreSerializedAiPromptTemplateProperties;

const TemplateDraftForm = formWithValidation<IAiPromptTemplateDraftFormValues, ClientAiPromptTemplateDraft>(
  (values) => plainToInstance(ClientAiPromptTemplateDraft, values),
  (entity) => instanceToPlain(entity) as IAiPromptTemplateDraftFormValues
);

const TemplateEntityForm = formWithValidation<ISyncCoreAiPromptTemplateDTO, ClientAiPromptTemplateEntity>(
  (values) => plainToInstance(ClientAiPromptTemplateEntity, values),
  (entity) => instanceToPlain(entity) as ISyncCoreAiPromptTemplateDTO
);

function defaultOutput(): SyncCoreAiPromptOutput {
  const output = new SyncCoreAiPromptOutput();
  output.format = AiPromptOutputFormat.Text;
  return output;
}

function serializedReference(id?: InternalId): ISerializedReference | undefined {
  if (!id) return undefined;
  return { _: 1, id };
}

function optionalSerializedReference(id?: InternalId): IOptionalSerializedReference | undefined {
  if (!id) return undefined;
  return { _: 1, id };
}

function getSubmitErrorMessage(error: unknown): string {
  if (!error) return '';

  const responseData = (error as any)?.response?.data;
  const payload = responseData ?? error;

  if (payload && typeof payload === 'object') {
    const message = (payload as any).message;
    const statusCode = (payload as any).statusCode;
    const errorLabel = (payload as any).error;

    if (Array.isArray(message)) {
      const lines = message
        .map((m) => {
          const property = (m as any)?.property;
          const constraints = (m as any)?.constraints;
          const constraintText = constraints && typeof constraints === 'object' ? Object.values(constraints).join(' ') : undefined;
          if (property && constraintText) return `${property}: ${constraintText}`;
          if (property) return String(property);
          return typeof m === 'string' ? m : JSON.stringify(m);
        })
        .filter(Boolean);

      const header = [statusCode ? String(statusCode) : undefined, errorLabel ? String(errorLabel) : undefined].filter(Boolean).join(' ');
      return `${header || 'Request failed'}\n${lines.join('\n')}`;
    }

    if (typeof message === 'string' && message.length) return message;
  }

  return error instanceof Error ? error.message : String(error);
}

function JsonTextarea({
  label,
  value,
  onChange,
  rows = 6,
}: {
  label: string;
  value: unknown;
  onChange: (next: unknown) => void;
  rows?: number;
}) {
  const [raw, setRaw] = useState(() => (value === undefined ? '' : JSON.stringify(value, null, 2)));
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <Row className="mb-3">
      <Col>
        <Form.Label className="fw-bold">{label}</Form.Label>
        <Form.Control
          as="textarea"
          rows={rows}
          value={raw}
          placeholder="Optional (JSON)"
          isInvalid={!!error}
          onChange={(e) => {
            const nextRaw = e.target.value;
            setRaw(nextRaw);
            if (!nextRaw.trim().length) {
              setError(undefined);
              onChange(undefined);
              return;
            }
            try {
              const parsed = JSON.parse(nextRaw);
              setError(undefined);
              onChange(parsed);
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            }
          }}
        />
        {error ? <Form.Control.Feedback type="invalid">Invalid JSON: {error}</Form.Control.Feedback> : null}
      </Col>
    </Row>
  );
}

export function AiPromptTemplateDraftEditor({
  api,
  initialValues,
  submitLabel,
  onSubmit,
}: {
  api: ISyncCoreApi;
  initialValues: ClientAiPromptTemplateDraft;
  submitLabel: string;
  onSubmit: (draft: ClientAiPromptTemplateDraft) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(undefined);

  return (
    <TemplateDraftForm
      initialValues={initialValues}
      onSubmit={async (draft) => {
        setSaving(true);
        setSubmitError(undefined);
        try {
          await onSubmit(draft);
        } catch (e) {
          setSubmitError(e);
        } finally {
          setSaving(false);
        }
      }}
    >
      {({ values, setValue }) => {
        const providerId = values.provider?.id;
        const modelId = values.model?.id;
        const customerId = values.customer?.id ?? '';
        const projectId = values.project?.id ?? '';

        const output = values.output ?? defaultOutput();

        return (
          <>
            {submitError ? (
              <Alert variant="danger" className="mb-3">
                <div className="fw-bold">Save failed</div>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {getSubmitErrorMessage(submitError)}
                </pre>
              </Alert>
            ) : null}
            <Row>
              <Col md={6}>
                <FormRow name={'name'} label={'Name'} labelPlacement={'above'} required>
                  <FormField name={'name'} type={'text'} noRow />
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'status'} label={'Status'} labelPlacement={'above'} required>
                  <Form.Select value={values.status} onChange={(e) => setValue('status', e.target.value as AiPromptTemplateStatus)}>
                    {enumValues(AiPromptTemplateStatus).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'accessScope'} label={'Access scope'} labelPlacement={'above'} required>
                  <Form.Select value={values.accessScope} onChange={(e) => setValue('accessScope', e.target.value as AccessScope)}>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormRow name={'key'} label={'Key'} labelPlacement={'above'} required>
                  <Form.Control value={values.key ?? ''} onChange={(e) => setValue('key', e.target.value || '')} placeholder="Required" />
                </FormRow>
              </Col>

              <Col md={2}>
                <FormRow name={'version'} label={'Version'} labelPlacement={'above'} required>
                  <FormField name={'version'} type={'number'} noRow />
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'useCase'} label={'Use case'} labelPlacement={'above'} required>
                  <Form.Select value={values.useCase} onChange={(e) => setValue('useCase', e.target.value as AiModelUseCase)}>
                    {enumValues(AiModelUseCase).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'frozen'} label={'Frozen'} labelPlacement={'above'}>
                  <Form.Check type="checkbox" label="" checked={values.frozen} onChange={(e) => setValue('frozen', e.target.checked)} />
                </FormRow>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <FormRow name={'provider'} label={'Provider'} labelPlacement={'above'} required>
                  <AiProviderSelector
                    api={api}
                    value={providerId}
                    onChange={(nextId) => setValue('provider', serializedReference(nextId))}
                    disabled={saving}
                    name="provider"
                  />
                </FormRow>
              </Col>
              <Col md={6}>
                <FormRow name={'model'} label={'Model'} labelPlacement={'above'} required>
                  <AiModelSelector
                    api={api}
                    value={modelId}
                    onChange={(nextId) => setValue('model', serializedReference(nextId))}
                    disabled={saving}
                    name="model"
                  />
                </FormRow>
              </Col>
            </Row>

            <FormRow name={'userMessage'} label={'User message'} labelPlacement={'above'} required>
              <FormField name={'userMessage'} rows={6} />
            </FormRow>

            <div className="fw-bold mb-1">System prompt (structured)</div>

            <FormRow name={'systemPrompt.structured.role'} label={'Role'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.role'} rows={2} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.context'} label={'Context'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.context'} rows={4} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.task'} label={'Task'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.task'} rows={3} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.input'} label={'Input'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.input'} rows={3} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.output'} label={'Output'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.output'} rows={3} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.developer'} label={'Developer'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.developer'} rows={6} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.uncertainty'} label={'Uncertainty'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.uncertainty'} rows={4} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.guardrails'} label={'Guardrails'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.guardrails'} rows={5} />
            </FormRow>

            <Row>
              <Col md={4}>
                <FormRow name={'tokensMax'} label={'Max tokens'} labelPlacement={'above'} required>
                  <FormField name={'tokensMax'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'tokensMin'} label={'Min tokens'} labelPlacement={'above'}>
                  <FormField name={'tokensMin'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'changeLog'} label={'Change log'} labelPlacement={'above'}>
                  <FormField name={'changeLog'} type={'text'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormRow name={'costsMin'} label={'Min costs'} labelPlacement={'above'}>
                  <FormField name={'costsMin'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={6}>
                <FormRow name={'costsMax'} label={'Max costs'} labelPlacement={'above'}>
                  <FormField name={'costsMax'} type={'number'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Label className="fw-bold">Output format</Form.Label>
                <Form.Select
                  value={output.format}
                  onChange={(e) => setValue('output', { ...output, format: e.target.value as AiPromptOutputFormat })}
                >
                  {enumValues(AiPromptOutputFormat).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label className="fw-bold">Text format</Form.Label>
                <Form.Select
                  value={output.textFormat ?? ''}
                  onChange={(e) =>
                    setValue('output', {
                      ...output,
                      textFormat: (e.target.value || undefined) as AiPromptOutputTextFormat | undefined,
                    })
                  }
                >
                  <option value="">(default)</option>
                  {enumValues(AiPromptOutputTextFormat).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            {output.format === AiPromptOutputFormat.Json ? (
              <JsonTextarea
                label="Output JSON schema"
                value={output.jsonSchema}
                onChange={(next) => setValue('output', { ...output, jsonSchema: next })}
              />
            ) : null}

            <JsonTextarea
              label="Default parameters"
              value={values.defaultParameters}
              onChange={(next) => setValue('defaultParameters', next ?? {})}
            />

            <JsonTextarea label="Variables" value={values.variables} onChange={(next) => setValue('variables', next ?? [])} />

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label className="fw-bold">Customer ID</Form.Label>
                <Form.Control
                  value={customerId}
                  onChange={(e) => {
                    const id = normalizeFilterId(e.target.value);
                    setValue('customer', optionalSerializedReference(id));
                  }}
                  placeholder="Optional"
                />
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold">Project ID</Form.Label>
                <Form.Control
                  value={projectId}
                  onChange={(e) => {
                    const id = normalizeFilterId(e.target.value);
                    setValue('project', optionalSerializedReference(id));
                  }}
                  placeholder="Optional"
                />
              </Col>
            </Row>

            <Right className="pe-1">
              <Button variant="primary" type="submit" disabled={saving}>
                {submitLabel}
              </Button>
            </Right>
          </>
        );
      }}
    </TemplateDraftForm>
  );
}

export function AiPromptTemplateEntityEditor({
  api,
  initialValues,
  submitLabel,
  onSubmit,
}: {
  api: ISyncCoreApi;
  initialValues: ClientAiPromptTemplateEntity;
  submitLabel: string;
  onSubmit: (entity: ClientAiPromptTemplateEntity) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(undefined);

  return (
    <TemplateEntityForm
      initialValues={initialValues}
      onSubmit={async (entity) => {
        setSaving(true);
        setSubmitError(undefined);
        try {
          await onSubmit(entity);
        } catch (e) {
          setSubmitError(e);
        } finally {
          setSaving(false);
        }
      }}
    >
      {({ values, setValue }) => {
        const providerId = values.provider?.id;
        const modelId = values.model?.id;
        const customerId = values.customer?.id ?? '';
        const projectId = values.project?.id ?? '';

        const output = values.output ?? defaultOutput();

        return (
          <>
            {submitError ? (
              <Alert variant="danger" className="mb-3">
                <div className="fw-bold">Save failed</div>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {getSubmitErrorMessage(submitError)}
                </pre>
              </Alert>
            ) : null}
            <Row>
              <Col md={6}>
                <FormRow name={'name'} label={'Name'} labelPlacement={'above'} required>
                  <FormField name={'name'} type={'text'} noRow />
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'status'} label={'Status'} labelPlacement={'above'} required>
                  <Form.Select value={values.status} onChange={(e) => setValue('status', e.target.value as AiPromptTemplateStatus)}>
                    {enumValues(AiPromptTemplateStatus).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'accessScope'} label={'Access scope'} labelPlacement={'above'} required>
                  <Form.Select value={values.accessScope} onChange={(e) => setValue('accessScope', e.target.value as AccessScope)}>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormRow name={'key'} label={'Key'} labelPlacement={'above'} required>
                  <Form.Control value={values.key ?? ''} onChange={(e) => setValue('key', e.target.value || '')} placeholder="Required" />
                </FormRow>
              </Col>

              <Col md={2}>
                <FormRow name={'version'} label={'Version'} labelPlacement={'above'} required>
                  <FormField name={'version'} type={'number'} noRow />
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'useCase'} label={'Use case'} labelPlacement={'above'} required>
                  <Form.Select value={values.useCase} onChange={(e) => setValue('useCase', e.target.value as AiModelUseCase)}>
                    {enumValues(AiModelUseCase).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'frozen'} label={'Frozen'} labelPlacement={'above'}>
                  <Form.Check type="checkbox" label="" checked={values.frozen} onChange={(e) => setValue('frozen', e.target.checked)} />
                </FormRow>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <FormRow name={'provider'} label={'Provider'} labelPlacement={'above'} required>
                  <AiProviderSelector
                    api={api}
                    value={providerId}
                    onChange={(nextId) => setValue('provider', serializedReference(nextId))}
                    disabled={saving}
                    name="provider"
                  />
                </FormRow>
              </Col>
              <Col md={6}>
                <FormRow name={'model'} label={'Model'} labelPlacement={'above'} required>
                  <AiModelSelector
                    api={api}
                    value={modelId}
                    onChange={(nextId) => setValue('model', serializedReference(nextId))}
                    disabled={saving}
                    name="model"
                  />
                </FormRow>
              </Col>
            </Row>

            <FormRow name={'userMessage'} label={'User message'} labelPlacement={'above'} required>
              <FormField name={'userMessage'} rows={6} />
            </FormRow>

            <div className="fw-bold mb-1">System prompt (structured)</div>

            <FormRow name={'systemPrompt.structured.role'} label={'Role'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.role'} rows={2} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.context'} label={'Context'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.context'} rows={4} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.task'} label={'Task'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.task'} rows={3} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.input'} label={'Input'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.input'} rows={3} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.output'} label={'Output'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.output'} rows={3} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.developer'} label={'Developer'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.developer'} rows={6} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.uncertainty'} label={'Uncertainty'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.uncertainty'} rows={4} />
            </FormRow>

            <FormRow name={'systemPrompt.structured.guardrails'} label={'Guardrails'} labelPlacement={'above'}>
              <FormField name={'systemPrompt.structured.guardrails'} rows={5} />
            </FormRow>

            <Row>
              <Col md={4}>
                <FormRow name={'tokensMax'} label={'Max tokens'} labelPlacement={'above'} required>
                  <FormField name={'tokensMax'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'tokensMin'} label={'Min tokens'} labelPlacement={'above'}>
                  <FormField name={'tokensMin'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={4}>
                <FormRow name={'changeLog'} label={'Change log'} labelPlacement={'above'}>
                  <FormField name={'changeLog'} type={'text'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormRow name={'costsMin'} label={'Min costs'} labelPlacement={'above'}>
                  <FormField name={'costsMin'} type={'number'} noRow />
                </FormRow>
              </Col>
              <Col md={6}>
                <FormRow name={'costsMax'} label={'Max costs'} labelPlacement={'above'}>
                  <FormField name={'costsMax'} type={'number'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Label className="fw-bold">Output format</Form.Label>
                <Form.Select
                  value={output.format}
                  onChange={(e) => setValue('output', { ...output, format: e.target.value as AiPromptOutputFormat })}
                >
                  {enumValues(AiPromptOutputFormat).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label className="fw-bold">Text format</Form.Label>
                <Form.Select
                  value={output.textFormat ?? ''}
                  onChange={(e) =>
                    setValue('output', {
                      ...output,
                      textFormat: (e.target.value || undefined) as AiPromptOutputTextFormat | undefined,
                    })
                  }
                >
                  <option value="">(default)</option>
                  {enumValues(AiPromptOutputTextFormat).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            {output.format === AiPromptOutputFormat.Json ? (
              <JsonTextarea
                label="Output JSON schema"
                value={output.jsonSchema}
                onChange={(next) => setValue('output', { ...output, jsonSchema: next })}
              />
            ) : null}

            <JsonTextarea
              label="Default parameters"
              value={values.defaultParameters}
              onChange={(next) => setValue('defaultParameters', next ?? {})}
            />

            <JsonTextarea label="Variables" value={values.variables} onChange={(next) => setValue('variables', next ?? [])} />

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label className="fw-bold">Customer ID</Form.Label>
                <Form.Control
                  value={customerId}
                  onChange={(e) => {
                    const id = normalizeFilterId(e.target.value);
                    setValue('customer', optionalSerializedReference(id));
                  }}
                  placeholder="Optional"
                />
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold">Project ID</Form.Label>
                <Form.Control
                  value={projectId}
                  onChange={(e) => {
                    const id = normalizeFilterId(e.target.value);
                    setValue('project', optionalSerializedReference(id));
                  }}
                  placeholder="Optional"
                />
              </Col>
            </Row>

            <Right className="pe-1">
              <Button variant="primary" type="submit" disabled={saving}>
                {submitLabel}
              </Button>
            </Right>
          </>
        );
      }}
    </TemplateEntityForm>
  );
}

export function ensurePromptTemplateDraftDefaults(draft: ClientAiPromptTemplateDraft) {
  if (!draft.variables) draft.variables = [];
  if (!draft.defaultParameters) draft.defaultParameters = {};
  if (!draft.output) draft.output = defaultOutput();
  if (!draft.systemPrompt) draft.systemPrompt = {} as any;
  if (!(draft.systemPrompt as any).structured) (draft.systemPrompt as any).structured = {};
  if (draft.version === undefined || draft.version === null) draft.version = 1;
  if (!draft.status) draft.status = AiPromptTemplateStatus.Draft;
}

export function ensurePromptTemplateEntityDefaults(entity: ClientAiPromptTemplateEntity) {
  if (!entity.output) entity.output = defaultOutput();
  if (!entity.systemPrompt) entity.systemPrompt = {} as any;
  if (!(entity.systemPrompt as any).structured) (entity.systemPrompt as any).structured = {};
}
